/**
 * useCases — consumer hook for `CasesReaderPort` (034 US1; 039 US1 pagination,
 * US2 localized labels).
 *
 * The FIRST page is read via `React.use()` and SUSPENDS on its initial mount,
 * exactly as before. Promise-caching discipline is identical to `useMyData`
 * (033) / the 034 original: the production port is genuinely ASYNC (the tRPC
 * reader), and a component that suspends on its INITIAL mount has its fiber
 * discarded — a `useRef`-cached promise would be recreated every retry and the
 * boundary would loop forever. The cache therefore lives OUTSIDE React in a
 * module-level WeakMap keyed by the port instance, then by the active LANGUAGE
 * (039 US2): it survives the suspend-retry, resets when the composition swaps
 * the adapter (new port key) OR the member switches language (a fresh read in
 * the new language — FR-011), and is GC'd with the port. The list re-fetches on
 * reload (route-on-read — no local copy, FR-006).
 *
 * SUBSEQUENT pages (039 US1 "load more") are NON-suspending: `loadMore()` fetches
 * the next cursor and APPENDS to component-local state — it never re-suspends the
 * boundary (re-suspending would blank the already-shown list, breaking FR-005).
 * The accumulated pages are ephemeral component state discarded on unmount/reload
 * (route-on-read; no mirror — FR-006). A failed load-more keeps the loaded list
 * and surfaces a retryable error (FR-005) without dropping any case.
 *
 * Only call inside a component wrapped in a Suspense boundary; `CasesList` wraps
 * its list body in `PortErrorBoundary(memberCases)` + Suspense.
 *
 * 052 US1: the module also exports `invalidateCasesCache(port)` — after a
 * successful compose send it evicts the port's cached first page AND bumps a
 * per-port invalidation generation, so the member's next list view is a fresh
 * route-on-read that includes the new case and a still-mounted consumer resets
 * its accumulated pages (no stale+fresh merge — FR-003). See that function's doc.
 *
 * 055 FR-004: CaseDetail also invalidates after a mark-as-read on open that may
 * have committed marks (ok, unavailable, or a rejected call), so the list row's
 * per-conversation unread indication (D-230) is re-read fresh rather than served
 * from the pre-read cache.
 */

import React, { useContext, useRef, useState } from 'react';
import type { LanguageCode } from '../i18n/LanguagePort.js';
import { PortsContext } from '../providers/PortsContext.js';
import type { CaseSummaryView, CasesReaderPort, CasesView } from './CasesReaderPort.js';

interface CasesCacheEntry {
  readonly promise: PromiseLike<CasesView>;
  rejected: boolean;
}

// Keyed by port instance, then by the active language (039 US2 re-read on switch).
const casesPromiseCache = new WeakMap<CasesReaderPort, Map<LanguageCode, CasesCacheEntry>>();

// Monotonic invalidation generation per port (052 US1). `invalidateCasesCache`
// bumps it so a consumer that is STILL MOUNTED across the invalidation (the
// Viestit compose is an OVERLAY, so `CasesList` stays mounted underneath — it
// re-renders, it does not remount) resets its accumulated "load more" pages on
// the consequent fresh read. Without this, the fresh first page would merge with
// a now-stale appended tail (a duplicated/misordered list — 052 FR-003). A
// remount resets accumulation anyway (fresh component state); this counter
// covers the re-render path the overlay actually takes.
const casesInvalidationGeneration = new WeakMap<CasesReaderPort, number>();

/**
 * Evict the cached cases list for a port (052 US1). After a successful compose
 * send, the caller invalidates so the member's NEXT `useCases` read for this
 * port is a fresh route-on-read that includes the just-created case — never a
 * fabricated local row (P7). Dropping the whole per-port map evicts all cached
 * languages for that port.
 *
 * TWO effects (both idempotent in result, neither a strict no-op): it (1) drops
 * the port's promise-cache entry so the next read re-fetches, and (2) bumps the
 * port's invalidation generation so a STILL-MOUNTED consumer resets its
 * accumulated "load more" pages on the re-read (no stale+fresh merge — 052
 * FR-003). Calling it on a port with no cache entry still bumps the generation;
 * that is harmless (a consumer with no accumulation has nothing to reset).
 *
 * NOTE (overlay re-suspend — see 052 research Decision 1): on the Viestit page
 * the compose wizard is an OVERLAY, so `CasesList` stays mounted underneath. The
 * caller must trigger the consequent re-read inside a `startTransition` (or via a
 * non-suspending refetch) so the mounted list is not torn down to its Suspense
 * fallback with focus dropped to `<body>` (052 FR-004); a bare
 * `invalidateCasesCache` alone only clears the cache and marks the reset, it does
 * not by itself make the re-read concurrent. The compose flow wraps its close in
 * a transition at `ComposeWizard`'s single close seam.
 */
export function invalidateCasesCache(port: CasesReaderPort): void {
  casesPromiseCache.delete(port);
  casesInvalidationGeneration.set(port, (casesInvalidationGeneration.get(port) ?? 0) + 1);
}

function isPromiseLike(value: CasesView | PromiseLike<CasesView>): value is PromiseLike<CasesView> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

/**
 * The paged cases view the surface renders (039 US1). `cases` is the ACCUMULATED
 * list (first page + every appended page). `hasMore` reflects whether a further
 * page can be loaded; `loadMore()` fetches it (a no-op when there is no more or
 * a load is already in flight). `loadingMore`/`loadMoreError` drive the honest
 * in-flight / retryable-failure affordances — never a re-suspend.
 */
export type PagedCasesView =
  | {
      readonly status: 'ok';
      readonly cases: readonly CaseSummaryView[];
      readonly hasMore: boolean;
      readonly loadingMore: boolean;
      readonly loadMoreError: boolean;
      readonly loadMore: () => void;
    }
  | { readonly status: 'unavailable' };

export function useCases(language: LanguageCode): PagedCasesView {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useCases: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.memberCases === undefined) {
    throw new Error(
      'useCases: memberCases port is not registered. This feature requires the member-cases port to be configured.',
    );
  }

  const port = ports.memberCases;

  // Resolve the FIRST page for (port, language). `React.use` is deliberately
  // called from within a conditional (React permits `use` in conditionals,
  // unlike other Hooks); the synchronous test-fixture path returns a resolved
  // view without suspending.
  let byLanguage = casesPromiseCache.get(port);
  if (byLanguage === undefined) {
    byLanguage = new Map<LanguageCode, CasesCacheEntry>();
    casesPromiseCache.set(port, byLanguage);
  }

  let firstView: CasesView;
  let entry = byLanguage.get(language);
  if (entry === undefined) {
    const viewOrPromise = port.getCases(undefined, language);
    if (!isPromiseLike(viewOrPromise)) {
      firstView = viewOrPromise;
    } else {
      const newEntry: CasesCacheEntry = { promise: viewOrPromise, rejected: false };
      viewOrPromise.then(undefined, () => {
        newEntry.rejected = true;
      });
      byLanguage.set(language, newEntry);
      entry = newEntry;
      firstView = React.use(newEntry.promise);
    }
  } else {
    if (entry.rejected) {
      // Deferred eviction (the useMyData Fix-1 rationale): a permanently-cached
      // rejected promise would brick the surface on every later mount, but eager
      // eviction makes React's synchronous error-retry re-fetch in an unbounded
      // loop. queueMicrotask lands the eviction AFTER React commits the boundary
      // fallback — retries still observe the rejected entry; a later mount finds
      // an empty cache and re-fetches.
      const scoped = byLanguage;
      queueMicrotask(() => scoped.delete(language));
    }
    firstView = React.use(entry.promise);
  }

  const firstCursor = firstView.status === 'ok' ? (firstView.nextCursor ?? null) : null;

  // ── Load-more state (ephemeral; discarded on unmount/reload — route-on-read) ─
  const [appended, setAppended] = useState<readonly CaseSummaryView[]>([]);
  const [cursor, setCursor] = useState<string | null>(firstCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  // Synchronous in-flight guard: an `IntersectionObserver` and a "Load more"
  // click can both fire before `setLoadingMore(true)` commits, so the ref (not
  // the async state) is what dedupes rapid triggers — no duplicate requests, no
  // skipped/duplicated cases across pages (FR-007).
  const loadingRef = useRef(false);
  // Monotonic generation for the (port, language) identity. Bumped on every reset
  // so an in-flight load-more that started under a PRIOR identity can detect that
  // it is now stale and drop its result instead of polluting the fresh read.
  const generationRef = useRef(0);

  // Reset the accumulation when the port instance OR the language changes (a
  // composition swap, a route reload, or a language switch → a fresh read), OR
  // when the port's invalidation generation was bumped (052 US1: a send evicted
  // the cache while this consumer stayed mounted — the overlay path). The
  // render-phase set is the sanctioned "adjust state when a dependency changes"
  // pattern (it re-renders immediately).
  const invalidationGeneration = casesInvalidationGeneration.get(port) ?? 0;
  const keyRef = useRef<{
    port: CasesReaderPort;
    language: LanguageCode;
    invalidationGeneration: number;
  }>({ port, language, invalidationGeneration });
  if (
    keyRef.current.port !== port ||
    keyRef.current.language !== language ||
    keyRef.current.invalidationGeneration !== invalidationGeneration
  ) {
    keyRef.current = { port, language, invalidationGeneration };
    generationRef.current += 1;
    loadingRef.current = false;
    setAppended([]);
    setCursor(firstCursor);
    setLoadingMore(false);
    setLoadMoreError(false);
  }

  // Plain function — React Compiler memoizes it (no manual `useCallback`, per the
  // react-compiler-uniformity rule). The synchronous `loadingRef` guard, not the
  // function identity, is what dedupes rapid triggers.
  const loadMore = (): void => {
    if (loadingRef.current || cursor === null) {
      return;
    }
    loadingRef.current = true;
    // Capture the identity this load belongs to; if a language/port switch bumps
    // the generation while this fetch is in flight, its result is stale and must
    // NOT be applied (else old-language cases append onto the fresh read and the
    // cursor is corrupted — FR-007/FR-011).
    const generation = generationRef.current;
    setLoadingMore(true);
    setLoadMoreError(false);
    void (async () => {
      try {
        const next = await Promise.resolve(port.getCases(cursor, language));
        if (generation !== generationRef.current) {
          return; // stale — a language/port switch superseded this load
        }
        if (next.status === 'ok') {
          setAppended((prev) => [...prev, ...next.cases]);
          setCursor(next.nextCursor ?? null);
        } else {
          // An honest "couldn't load more" — the loaded list stays put; the
          // same cursor is retained so a retry re-requests the same page.
          setLoadMoreError(true);
        }
      } catch {
        if (generation !== generationRef.current) {
          return; // stale — ignore a superseded load's failure too
        }
        setLoadMoreError(true);
      } finally {
        // Only clear the in-flight flags if THIS load still owns the identity —
        // a superseded load must not reset the fresh read's flags.
        if (generation === generationRef.current) {
          loadingRef.current = false;
          setLoadingMore(false);
        }
      }
    })();
  };

  if (firstView.status !== 'ok') {
    return { status: 'unavailable' };
  }

  return {
    status: 'ok',
    cases: [...firstView.cases, ...appended],
    hasMore: cursor !== null,
    loadingMore,
    loadMoreError,
    loadMore,
  };
}
